import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from './config/apiConfig';
import './ExploreUnknownDevExperiencePanel.css';

const QUICK_LEVELS = [1, 5, 10, 20, 45, 50, 100];

/**
 * Dev-only (player1): set Explore Unknown account XP/level for testing.
 */
export default function ExploreUnknownDevExperiencePanel({
  currentLevel,
  currentTotalXp,
  ensureValidToken,
  onApplied,
}) {
  const [levelInput, setLevelInput] = useState(String(currentLevel ?? 1));
  const [xpInput, setXpInput] = useState(String(currentTotalXp ?? 0));
  const [mode, setMode] = useState('level');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLevelInput(String(currentLevel ?? 1));
    setXpInput(String(currentTotalXp ?? 0));
  }, [currentLevel, currentTotalXp]);

  const applyExperience = useCallback(
    async (payload) => {
      setBusy(true);
      setError(null);
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/explore-unknown/dev-set-experience`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || `Set experience failed (${response.status})`);
        }
        onApplied(data);
        if (data.exploreUnknownLevel != null) {
          setLevelInput(String(data.exploreUnknownLevel));
        }
        if (data.exploreUnknownTotalXp != null) {
          setXpInput(String(data.exploreUnknownTotalXp));
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        throw err;
      } finally {
        setBusy(false);
      }
    },
    [ensureValidToken, onApplied]
  );

  const handleApply = () => {
    if (mode === 'level') {
      const lv = parseInt(levelInput, 10);
      if (Number.isNaN(lv) || lv < 1 || lv > 100) {
        setError('Level must be 1–100');
        return;
      }
      void applyExperience({ level: lv }).catch(() => {});
      return;
    }
    const xp = parseInt(xpInput, 10);
    if (Number.isNaN(xp) || xp < 0) {
      setError('Total XP must be a non-negative integer');
      return;
    }
    void applyExperience({ totalXp: xp }).catch(() => {});
  };

  return (
    <div className="explore-unknown-dev-experience" aria-label="Dev: set Explore Unknown experience">
      <span className="explore-unknown-dev-experience-title">Dev: set experience</span>
      <div className="explore-unknown-dev-experience-mode">
        <label>
          <input
            type="radio"
            name="eu-dev-exp-mode"
            checked={mode === 'level'}
            onChange={() => setMode('level')}
          />
          Level
        </label>
        <label>
          <input
            type="radio"
            name="eu-dev-exp-mode"
            checked={mode === 'xp'}
            onChange={() => setMode('xp')}
          />
          Total XP
        </label>
      </div>
      {mode === 'level' ? (
        <label className="explore-unknown-dev-experience-field">
          <span>Level (1–100)</span>
          <input
            type="number"
            min={1}
            max={100}
            value={levelInput}
            disabled={busy}
            onChange={(e) => setLevelInput(e.target.value)}
          />
        </label>
      ) : (
        <label className="explore-unknown-dev-experience-field">
          <span>Total XP</span>
          <input
            type="number"
            min={0}
            value={xpInput}
            disabled={busy}
            onChange={(e) => setXpInput(e.target.value)}
          />
        </label>
      )}
      <button type="button" className="explore-unknown-dev-experience-apply" disabled={busy} onClick={handleApply}>
        {busy ? '…' : 'Apply'}
      </button>
      <div className="explore-unknown-dev-experience-quick" aria-label="Quick level presets">
        {QUICK_LEVELS.map((lv) => (
          <button
            key={lv}
            type="button"
            className="explore-unknown-dev-experience-quick-btn"
            disabled={busy}
            onClick={() => {
              setMode('level');
              setLevelInput(String(lv));
              void applyExperience({ level: lv }).catch(() => {});
            }}
          >
            L{lv}
          </button>
        ))}
      </div>
      {error ? (
        <p className="explore-unknown-dev-experience-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
